/**
 * SMS — 各角色嘅底部 Tab 定義（P1.5-A；`decisions/015` §2.1）
 *
 * ⭐ 呢個檔 ＝ **「邊個角色見到邊 4 個功能」嘅唯一真相源**（前端）。
 *    後端一樣有權限閘（每個 route 自己 `guard()`）—— 前端 tab 只係「唔好畀人見到唔關佢事嘅嘢」。
 *
 * ⚠️ 舊 route（`/intakes`、`/inventory`、`/weekly-fees`、`/outbound`…）**一律保留**；
 *    新 route 係**額外入口**（舊書籤／WhatsApp 深連結唔會死）。
 */
import type { TabDef } from '@coeldery/role-tab-bar';
import type { Role } from './api';

export interface RoleTab extends TabDef {
  /** 撳呢個 tab 去邊 */
  to: string;
  /** 呢啲 path 前綴都當係呢個 tab（active 判斷；例 `/intakes/new` 屬「出貨」） */
  match: readonly string[];
}

const TABS: Record<Role, readonly RoleTab[]> = {
  // 供應商：我嘅資料 → 訂單 → 出貨 → 庫存
  supplier: [
    { id: 'me', labelKey: 'tab.me', icon: 'me', to: '/me', match: ['/me'] },
    { id: 'orders', labelKey: 'tab.orders', icon: 'orders', to: '/orders', match: ['/orders'] },
    {
      id: 'shipment',
      labelKey: 'tab.shipment',
      icon: 'shipment',
      to: '/intakes',
      match: ['/intakes', '/shipments'],
    },
    { id: 'stock', labelKey: 'tab.stock', icon: 'stock', to: '/stock', match: ['/stock', '/inventory'] },
  ],
  // 物流：到貨 → 出貨指示 → 庫存 → 倉租
  logistics: [
    { id: 'incoming', labelKey: 'tab.incoming', icon: 'incoming', to: '/incoming', match: ['/incoming', '/intakes'] },
    { id: 'pickup', labelKey: 'tab.pickup', icon: 'pickup', to: '/pickup', match: ['/pickup', '/outbound', '/o/'] },
    { id: 'stock', labelKey: 'tab.stock', icon: 'stock', to: '/stock', match: ['/stock', '/inventory'] },
    { id: 'fees', labelKey: 'tab.fees', icon: 'fees', to: '/fees', match: ['/fees', '/weekly-fees'] },
  ],
  // 職員：總覽 → 採購 → 出貨 → 貨去向
  staff: [
    { id: 'overview', labelKey: 'tab.overview', icon: 'home', to: '/home', match: ['/home', '/'] },
    { id: 'purchase', labelKey: 'tab.purchase', icon: 'purchase', to: '/purchase', match: ['/purchase'] },
    { id: 'outbound', labelKey: 'tab.outbound', icon: 'outbound', to: '/outbound', match: ['/outbound', '/o/'] },
    {
      id: 'disposition',
      labelKey: 'tab.disposition',
      icon: 'disposition',
      to: '/disposition',
      match: ['/disposition', '/pending'],
    },
  ],
  admin: [
    { id: 'overview', labelKey: 'tab.overview', icon: 'home', to: '/home', match: ['/home', '/'] },
    { id: 'purchase', labelKey: 'tab.purchase', icon: 'purchase', to: '/purchase', match: ['/purchase'] },
    { id: 'outbound', labelKey: 'tab.outbound', icon: 'outbound', to: '/outbound', match: ['/outbound', '/o/'] },
    {
      id: 'disposition',
      labelKey: 'tab.disposition',
      icon: 'disposition',
      to: '/disposition',
      match: ['/disposition', '/pending'],
    },
  ],
};

/** 呢個角色嘅 tabs（**次序 ＝ 畫面次序**） */
export const tabsFor = (role: Role): readonly RoleTab[] => TABS[role] ?? TABS.staff;

/**
 * 由而家路徑搵出 active tab（搵唔到 → 第一個 tab）。
 * ⚠️ 用**最長前綴**配對（`/outbound/new` 會中 `/outbound`；`/o/SO-1` 會中 `/o/`）。
 */
export function activeTabId(role: Role, pathname: string): string {
  const tabs = tabsFor(role);
  let best: { id: string; len: number } | null = null;
  for (const tb of tabs) {
    for (const m of tb.match) {
      const hit = m.endsWith('/') ? pathname.startsWith(m) : pathname === m || pathname.startsWith(`${m}/`);
      if (hit && (!best || m.length > best.len)) best = { id: tb.id, len: m.length };
    }
  }
  return best?.id ?? tabs[0].id;
}
